import { lazy, Suspense, type ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { PageSkeleton } from '@/components/ui/States'
import { RequireAuth, RequireRole } from '@/routes/guards'
import RoleDashboardPage from '@/pages/shared/RoleDashboardPage'
import PortalListPage from '@/pages/shared/PortalListPage'
import {
  DeptAdminApplications,
  DeptAdminAssessments,
  DeptAdminAssignUnits,
  DeptAdminAttachments,
  DeptAdminAttendance,
  DeptAdminClasses,
  DeptAdminCompanies,
  DeptAdminCourses,
  DeptAdminDashboard,
  DeptAdminExamBookings,
  DeptAdminLogbooks,
  DeptAdminMarks,
  DeptAdminStudents,
  DeptAdminTraineePoe,
  DeptAdminTrainers,
  DeptAdminUnits,
  SuperAdminDashboard,
  SuperAdminDepartments,
  SuperAdminLogs,
  SuperAdminUsers,
} from '@/pages/admin/PortalPages'

const LoginPage = lazy(() => import('@/pages/auth/LoginPage'))
const ApplyPage = lazy(() => import('@/pages/public/ApplyPage'))
const TrainerDashboardPage = lazy(() => import('@/pages/trainer/DashboardPage'))
const MarksEntryPage = lazy(() => import('@/pages/trainer/MarksEntryPage'))
const AssessmentsPage = lazy(() => import('@/pages/trainer/AssessmentsPage'))
const AttendancePage = lazy(() => import('@/pages/trainer/AttendancePage'))
const AttendanceHistoryPage = lazy(() => import('@/pages/trainer/AttendanceHistoryPage'))
const PortfolioPage = lazy(() => import('@/pages/trainer/PortfolioPage'))
const MarksImportPage = lazy(() => import('@/pages/trainer/MarksImportPage'))
const BiometricPage = lazy(() => import('@/pages/trainer/BiometricPage'))
const SummativeHubPage = lazy(() => import('@/pages/trainer/SummativeHubPage'))
const SummativeAnalysisPage = lazy(() => import('@/pages/trainer/SummativeAnalysisPage'))
const TripsPage = lazy(() => import('@/pages/trainer/TripsPage'))
const StudentDashboardPage = lazy(() => import('@/pages/student/DashboardPage'))
const StudentAttendancePage = lazy(() => import('@/pages/student/AttendancePage'))
const StudentUnitsPage = lazy(() => import('@/pages/student/UnitsPage'))
const StudentMarksPage = lazy(() => import('@/pages/student/MarksPage'))
const StudentAssessmentsPage = lazy(() => import('@/pages/student/AssessmentsPage'))
const AssessmentUploadPage = lazy(() => import('@/pages/student/AssessmentUploadPage'))
const AssessmentEvidencePage = lazy(() => import('@/pages/student/AssessmentEvidencePage'))
const StudentPortfolioPage = lazy(() => import('@/pages/student/PortfolioPage'))
const StudentDocumentsPage = lazy(() => import('@/pages/student/DocumentsPage'))
const StudentExamBookingsPage = lazy(() => import('@/pages/student/ExamBookingsPage'))
const ExamBookingFormPage = lazy(() => import('@/pages/student/ExamBookingFormPage'))
const IndustrialAttachmentPage = lazy(() => import('@/pages/student/IndustrialAttachmentPage'))
const StudentLogbookPage = lazy(() => import('@/pages/student/LogbookPage'))
const AttachmentMarksPage = lazy(() => import('@/pages/student/AttachmentMarksPage'))
const MentoringToolPage = lazy(() => import('@/pages/student/MentoringToolPage'))
const EmploymentStatusPage = lazy(() => import('@/pages/student/EmploymentStatusPage'))
const NotificationsPage = lazy(() => import('@/pages/shared/NotificationsPage'))
const ProfilePage = lazy(() => import('@/pages/shared/ProfilePage'))
const StudentClearancePage = lazy(() => import('@/pages/clearance/StudentClearancePage'))
const ApproverClearancePage = lazy(() => import('@/pages/clearance/ApproverClearancePage'))

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <Routes>
          <Route
            path="/login"
            element={
              <Lazy>
                <LoginPage />
              </Lazy>
            }
          />

          <Route element={<RequireAuth />}>
            <Route
              path="/notifications"
              element={
                <Lazy>
                  <NotificationsPage />
                </Lazy>
              }
            />
            <Route
              path="/profile"
              element={
                <Lazy>
                  <ProfilePage />
                </Lazy>
              }
            />
            <Route
              path="/clearance/approver"
              element={
                <Lazy>
                  <ApproverClearancePage />
                </Lazy>
              }
            />

            <Route element={<RequireRole roles={['trainer']} />}>
              <Route path="/trainer/dashboard" element={<Lazy><TrainerDashboardPage /></Lazy>} />
              <Route path="/trainer/marks-entry" element={<Lazy><MarksEntryPage /></Lazy>} />
              <Route path="/trainer/assessments" element={<Lazy><AssessmentsPage /></Lazy>} />
              <Route path="/trainer/attendance" element={<Lazy><AttendancePage /></Lazy>} />
              <Route path="/trainer/attendance-history" element={<Lazy><AttendanceHistoryPage /></Lazy>} />
              <Route path="/trainer/portfolio" element={<Lazy><PortfolioPage /></Lazy>} />
              <Route path="/trainer/marks-import" element={<Lazy><MarksImportPage /></Lazy>} />
              <Route path="/trainer/biometric" element={<Lazy><BiometricPage /></Lazy>} />
              <Route path="/trainer" element={<Navigate to="/trainer/dashboard" replace />} />
            </Route>

            <Route element={<RequireRole roles={['student']} />}>
              <Route path="/student/dashboard" element={<Lazy><StudentDashboardPage /></Lazy>} />
              <Route path="/student/units" element={<Lazy><StudentUnitsPage /></Lazy>} />
              <Route path="/student/attendance" element={<Lazy><StudentAttendancePage /></Lazy>} />
              <Route path="/student/marks" element={<Lazy><StudentMarksPage /></Lazy>} />
              <Route path="/student/portfolio" element={<Lazy><StudentPortfolioPage /></Lazy>} />
              <Route path="/student/assessments" element={<Lazy><StudentAssessmentsPage /></Lazy>} />
              <Route path="/student/assessments/upload" element={<Lazy><AssessmentUploadPage /></Lazy>} />
              <Route path="/student/assessments/:assessmentId/evidence" element={<Lazy><AssessmentEvidencePage /></Lazy>} />
              <Route path="/student/documents" element={<Lazy><StudentDocumentsPage /></Lazy>} />
              <Route path="/student/exam-booking-form" element={<Lazy><ExamBookingFormPage /></Lazy>} />
              <Route path="/student/exam-bookings" element={<Lazy><StudentExamBookingsPage /></Lazy>} />
              <Route path="/student/industrial-attachment" element={<Lazy><IndustrialAttachmentPage /></Lazy>} />
              <Route path="/student/logbook" element={<Lazy><StudentLogbookPage /></Lazy>} />
              <Route path="/student/attachment-marks" element={<Lazy><AttachmentMarksPage /></Lazy>} />
              <Route path="/student/mentoring-tool" element={<Lazy><MentoringToolPage /></Lazy>} />
              <Route path="/student/employment-status" element={<Lazy><EmploymentStatusPage /></Lazy>} />
              <Route path="/clearance" element={<Lazy><StudentClearancePage /></Lazy>} />
              <Route path="/student" element={<Navigate to="/student/dashboard" replace />} />
            </Route>

            <Route element={<RequireRole roles={['trainer', 'dept_admin', 'super_admin']} />}>
              <Route path="/summative" element={<Lazy><SummativeHubPage /></Lazy>} />
              <Route path="/summative/analysis" element={<Lazy><SummativeAnalysisPage /></Lazy>} />
              <Route path="/academic-trips" element={<Lazy><TripsPage /></Lazy>} />
            </Route>

            <Route element={<RequireRole roles={['dept_admin', 'super_admin']} />}>
              <Route path="/dept-admin/dashboard" element={<DeptAdminDashboard />} />
              <Route path="/dept-admin/courses" element={<DeptAdminCourses />} />
              <Route path="/dept-admin/classes" element={<DeptAdminClasses />} />
              <Route path="/dept-admin/units" element={<DeptAdminUnits />} />
              <Route path="/dept-admin/trainers" element={<DeptAdminTrainers />} />
              <Route path="/dept-admin/students" element={<DeptAdminStudents />} />
              <Route path="/dept-admin/assign-units" element={<DeptAdminAssignUnits />} />
              <Route path="/dept-admin/attendance" element={<DeptAdminAttendance />} />
              <Route path="/dept-admin/assessments" element={<DeptAdminAssessments />} />
              <Route path="/dept-admin/exam-bookings" element={<DeptAdminExamBookings />} />
              <Route path="/dept-admin/marks" element={<DeptAdminMarks />} />
              <Route path="/dept-admin/trainee-poe" element={<DeptAdminTraineePoe />} />
              <Route path="/dept-admin/companies" element={<DeptAdminCompanies />} />
              <Route path="/dept-admin/attachments" element={<DeptAdminAttachments />} />
              <Route path="/dept-admin/logbooks" element={<DeptAdminLogbooks />} />
              <Route path="/dept-admin/applications" element={<DeptAdminApplications />} />
              <Route path="/dept-admin" element={<Navigate to="/dept-admin/dashboard" replace />} />
              <Route path="/summative/entry" element={<RoleDashboardPage title="Summative Entry" apiPath="/summative/entry" />} />
            </Route>

            <Route element={<RequireRole roles={['super_admin']} />}>
              <Route path="/super-admin/dashboard" element={<SuperAdminDashboard />} />
              <Route path="/super-admin/departments" element={<SuperAdminDepartments />} />
              <Route path="/super-admin/users" element={<SuperAdminUsers />} />
              <Route path="/super-admin/logs" element={<SuperAdminLogs />} />
              <Route path="/super-admin" element={<Navigate to="/super-admin/dashboard" replace />} />
            </Route>

            <Route element={<RequireRole roles={['examination_officer']} />}>
              <Route
                path="/examination-officer/dashboard"
                element={<RoleDashboardPage title="Examination Officer" apiPath="/examination-officer/dashboard" />}
              />
              <Route path="/examination-officer" element={<Navigate to="/examination-officer/dashboard" replace />} />
            </Route>

            <Route element={<RequireRole roles={['industry_mentor']} />}>
              <Route
                path="/industry-mentor/dashboard"
                element={<RoleDashboardPage title="Industry Mentor" apiPath="/industry-mentor/dashboard" />}
              />
              <Route path="/industry-mentor" element={<Navigate to="/industry-mentor/dashboard" replace />} />
            </Route>

            <Route element={<RequireRole roles={['internal_verifier']} />}>
              <Route
                path="/internal-verifier/dashboard"
                element={<RoleDashboardPage title="Internal Verifier" apiPath="/internal-verifier/dashboard" />}
              />
              <Route path="/internal-verifier" element={<Navigate to="/internal-verifier/dashboard" replace />} />
            </Route>

            <Route element={<RequireRole roles={['liaison_officer']} />}>
              <Route
                path="/liaison-officer/dashboard"
                element={
                  <RoleDashboardPage
                    title="Liaison Officer"
                    apiPath="/liaison-officer/dashboard"
                    links={[
                      { label: 'Attachments', to: '/liaison-officer/attachments' },
                      { label: 'Companies', to: '/liaison-officer/companies' },
                      { label: 'Logbooks', to: '/liaison-officer/logbooks' },
                    ]}
                  />
                }
              />
              <Route
                path="/liaison-officer/attachments"
                element={<PortalListPage title="Attachments" apiPath="/liaison-officer/attachments" listKey="attachments" columns={[{ key: 'status', label: 'Status' }, { key: 'start_date', label: 'Start' }]} />}
              />
              <Route
                path="/liaison-officer/companies"
                element={<PortalListPage title="Companies" apiPath="/liaison-officer/companies" listKey="companies" columns={[{ key: 'name', label: 'Name' }]} />}
              />
              <Route
                path="/liaison-officer/logbooks"
                element={<PortalListPage title="Logbooks" apiPath="/liaison-officer/logbooks" listKey="logbooks" columns={[{ key: 'log_date', label: 'Date' }]} />}
              />
              <Route path="/liaison-officer/periods" element={<RoleDashboardPage title="Periods" apiPath="/liaison-officer/periods" />} />
              <Route path="/liaison-officer/attachment-marks" element={<RoleDashboardPage title="Attachment Marks" apiPath="/liaison-officer/attachment-marks" />} />
              <Route path="/liaison-officer/mentoring-tools" element={<RoleDashboardPage title="Mentoring Tools" apiPath="/liaison-officer/mentoring-tools" />} />
              <Route path="/liaison-officer" element={<Navigate to="/liaison-officer/dashboard" replace />} />
            </Route>

            <Route element={<RequireRole roles={['cdacc_verifier']} />}>
              <Route path="/cdacc-verifier/dashboard" element={<RoleDashboardPage title="CDACC Verifier" apiPath="/cdacc-verifier/dashboard" />} />
              <Route path="/cdacc-verifier/assessments" element={<PortalListPage title="Assessments" apiPath="/cdacc-verifier/assessments" listKey="assessments" columns={[{ key: 'status', label: 'Status' }]} />} />
              <Route path="/cdacc-verifier/marks" element={<RoleDashboardPage title="Marks" apiPath="/cdacc-verifier/marks" />} />
              <Route path="/cdacc-verifier/trainee-poe" element={<RoleDashboardPage title="Trainee POE" apiPath="/cdacc-verifier/trainee-poe" />} />
              <Route path="/cdacc-verifier/trainees" element={<PortalListPage title="Trainees" apiPath="/cdacc-verifier/trainees" listKey="trainees" columns={[{ key: 'full_name', label: 'Name' }, { key: 'admission_no', label: 'Admission' }]} />} />
              <Route path="/cdacc-verifier/attachment-marks" element={<RoleDashboardPage title="Attachment Marks" apiPath="/cdacc-verifier/attachment-marks" />} />
              <Route path="/cdacc-verifier" element={<Navigate to="/cdacc-verifier/dashboard" replace />} />
            </Route>

            <Route element={<RequireRole roles={['workshop_technician']} />}>
              <Route path="/workshop-technician/dashboard" element={<RoleDashboardPage title="Workshop Technician" apiPath="/workshop-technician/dashboard" />} />
              <Route path="/workshop-technician" element={<Navigate to="/workshop-technician/dashboard" replace />} />
            </Route>

            <Route element={<RequireRole roles={['library_hod', 'sports_hod', 'service_clearance_officer']} />}>
              <Route path="/service-dept/dashboard" element={<RoleDashboardPage title="Service Department" apiPath="/service-dept/dashboard" />} />
              <Route path="/service-dept" element={<Navigate to="/service-dept/dashboard" replace />} />
            </Route>

            <Route element={<RequireRole roles={['registrar']} />}>
              <Route path="/admin-oversight/registrar" element={<RoleDashboardPage title="Registrar" apiPath="/admin-oversight/registrar" />} />
              <Route path="/admin-oversight/registrar/clearances" element={<RoleDashboardPage title="Registrar Clearances" apiPath="/admin-oversight/registrar/clearances" />} />
            </Route>

            <Route element={<RequireRole roles={['deputy_principal']} />}>
              <Route path="/admin-oversight/deputy-principal" element={<RoleDashboardPage title="Deputy Principal" apiPath="/admin-oversight/deputy-principal" />} />
              <Route path="/admin-oversight/deputy-principal/academic" element={<RoleDashboardPage title="Academic Oversight" apiPath="/admin-oversight/deputy-principal/academic" />} />
              <Route path="/admin-oversight/deputy-principal/clearances" element={<RoleDashboardPage title="Clearances" apiPath="/admin-oversight/deputy-principal/clearances" />} />
            </Route>

            <Route element={<RequireRole roles={['quality_assurance_officer']} />}>
              <Route path="/admin-oversight/quality-assurance" element={<RoleDashboardPage title="Quality Assurance" apiPath="/admin-oversight/quality-assurance" />} />
            </Route>
          </Route>

          <Route path="/apply" element={<Suspense fallback={<PageSkeleton />}><ApplyPage /></Suspense>} />
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </ErrorBoundary>
    </BrowserRouter>
  )
}
